import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import { Navigate } from 'react-router-dom';
import { getUser } from '../helpers';
import { makeProfileFlagMap } from '../helpers/helpers';

import { token as TOKEN } from "../constants";

const initialProfileRawData = {
  applications: {},
  tools: {},
  permissions: {},
};

const initialAuthState = {
  phase: 'init', // 'init' | 'ready' | 'anon'
  profile: {},
  profileRawData: initialProfileRawData,
  tabConfig: {},
  cactusToolConfigExists: null,
};

const AuthContext = createContext({
  profile: {},
  profileRawData: initialProfileRawData,
  tabConfig: {},
  cactusToolConfigExists: null,
  isAuthInProgress: true,
  isAuthenticated: false,
  login: async () => { },
  logout: () => { },
});

export const useAuth = () => useContext(AuthContext);

const createProfileFromData = (data = {}) => {
  const profileRawData = {
    applications: data.applications || {},
    tools: data.tools || {},
    permissions: data.permissions || {},
  };
  const profile = makeProfileFlagMap(profileRawData);

  return { profileRawData, profile };
};

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(initialAuthState);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem(TOKEN);
    if (!token) {
      setAuthState(prev => ({ ...prev, phase: 'anon' }));
      return null;
    }

    try {
      const response = await getUser();
      const { profileRawData, profile } = createProfileFromData(response.data);
      const { tabConfig, cactusToolConfigExists } = response.data || {};

      const newState = {
        phase: 'ready',
        profile,
        profileRawData,
        tabConfig,
        cactusToolConfigExists,
      };

      setAuthState(newState);

      return { profile, profileRawData, tabConfig };
    } catch (err) {
      console.error('Failed to load profile:', err);
      setAuthState(prev => ({ ...prev, phase: 'anon' }));
      return null;
    }
  }, []);

  const login = useCallback(async (auth) => {
    const { token, username } = auth.data || {};

    if (!token || !username) {
      throw new Error('Missing token or username');
    }

    localStorage.setItem(TOKEN, token);
    localStorage.setItem('username', username);

    const profileData = await loadProfile();

    if (!profileData) {
      throw new Error('Failed to load profile');
    }

    return profileData;
  }, [loadProfile]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem('username');

    setAuthState({
      phase: 'anon',
      profile: {},
      profileRawData: initialProfileRawData,
      tabConfig: {},
      cactusToolConfigExists: null
    });
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    window.addEventListener("force-logout", logout);
    return () => window.removeEventListener("force-logout", logout);
  }, [logout]);

  const contextValue = useMemo(() => ({
    profile: authState.profile,
    profileRawData: authState.profileRawData,
    tabConfig: authState.tabConfig,
    cactusToolConfigExists: authState.cactusToolConfigExists,
    isAuthInProgress: authState.phase === 'init',
    isAuthenticated: authState.phase === 'ready',
    login,
    logout,
  }), [authState, login, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const RequireAuth = ({ children, redirectTo = '/login', profileKey, tabConfigKey }) => {
  const { isAuthInProgress, isAuthenticated, profile, tabConfig } = useAuth();

  if (isAuthInProgress) return null;
  if (!isAuthenticated) return <Navigate to={redirectTo} />;
  if (profileKey && !profile[profileKey]) return <Navigate to={redirectTo} />;
  if (tabConfigKey && !tabConfig[tabConfigKey]) return <Navigate to={redirectTo} />;

  return <>{children}</>;
};
